import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { apiFetch } from '@/lib/api';
import { AbschnittRahmen } from './listenTeile';

/**
 * „Nutzung" — die Auswertung der anonymen Nutzungsmessung (02.11.2026).
 *
 * Fünf Listen über die letzten 30 Tage, jede mit dem, was man daraus tun
 * kann. Gezählt wird ohne Person (api/lib/nutzung.php); eine Zahl
 * erscheint erst ab fünf Ereignissen, ein Suchbegriff erst ab zwei
 * gleichen. Das sagt der Abschnitt auch selbst.
 */

type Zaehlung = { ort: string; anzahl: number };
export type NutzungAuswertung = {
  tage: number;
  mindestens: number;
  suchen: { begriff: string; ort: string; anzahl: number }[];
  sofort_zurueck: Zaehlung[];
  abgebrochen: Zaehlung[];
  warnungen: Zaehlung[];
  fehler: Zaehlung[];
  seiten: Zaehlung[];
  knoepfe: { knopf: string; anzahl: number | null }[];
  zeiten: { was: string; geraet: string; anzahl: number; mittel_ms: number }[];
};

const ORT: Record<string, string> = {
  dashboard: 'Dashboard',
  wareneingang: 'Wareneingang',
  erfassung_vlb: 'Erfassung (VLB)',
  bestellung: 'Bestellung',
  sendung_bereit: 'Sendung (beim Bereitsetzen)',
  sendung_anlegen: 'Sendung anlegen',
  versand: 'Abschluss-Warnung (Versand)',
  abholung: 'Abschluss-Warnung (Abholung)',
  archiv: 'Archiv',
  kanban: 'Kanban',
  reserviert: 'Reserviert',
  einstellungen: 'Einstellungen',
  zettel: 'Bestellzettel',
  lieferschein: 'Lieferschein',
  drucken: 'Drucken',
  login: 'Anmeldung'
};
const GERAET: Record<string, string> = { pc: 'PC', tablet: 'Tablet', handy: 'Handy' };

function sekunden(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s`;
}

function Liste({
  titel,
  wozu,
  leer,
  children
}: {
  titel: string;
  wozu: string;
  leer: boolean;
  children: ReactNode;
}) {
  return (
    <div className="rounded-md border border-line bg-cream p-3">
      <h3 className="text-sm font-medium text-ink">{titel}</h3>
      <p className="text-xs text-ink-faint">{wozu}</p>
      {leer ? (
        <p className="mt-2 text-sm text-ink-soft">Noch nichts — oder noch zu wenig, um es zu zeigen.</p>
      ) : (
        <div className="mt-2">{children}</div>
      )}
    </div>
  );
}

function Zeilen({ zeilen }: { zeilen: { links: string; rechts: string }[] }) {
  return (
    <ul className="divide-y divide-line text-sm">
      {zeilen.map((z) => (
        <li key={z.links} className="flex items-baseline justify-between gap-3 py-1.5">
          <span className="min-w-0 break-words text-ink">{z.links}</span>
          <span className="shrink-0 tabular-nums text-ink-soft">{z.rechts}</span>
        </li>
      ))}
    </ul>
  );
}

const zaehl = (z: Zaehlung[]) =>
  z.map((r) => ({ links: ORT[r.ort] ?? (r.ort || '—'), rechts: `${r.anzahl}×` }));

export function NutzungAbschnitt() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['nutzung', 'auswertung'],
    queryFn: () => apiFetch<{ auswertung: NutzungAuswertung }>('/nutzung/auswertung.php'),
    staleTime: 60_000
  });
  const a = data?.auswertung;

  return (
    <AbschnittRahmen
      ankerId="einst-nutzung"
      titel="Nutzung"
      einleitung="Wo die Arbeit hakt, in den letzten 30 Tagen. Gezählt wird anonym: ohne Namen, ohne Uhrzeit, nur Tag und Gerätetyp. Eine Zahl erscheint erst ab fünf Ereignissen, ein Suchbegriff erst, wenn er zweimal ohne Treffer blieb. Suchbegriffe werden nach 30 Tagen gelöscht, alles andere nach 90."
      laedt={isLoading}
      fehler={isError}
    >
      {a && (
        <div className="space-y-3">
          <Liste
            titel="Häufigste Suchen ohne Treffer"
            wozu="Suche verbessern oder fehlende Schreibweisen ergänzen."
            leer={a.suchen.length === 0}
          >
            <Zeilen
              zeilen={a.suchen.map((s) => ({
                links: `„${s.begriff}“ · ${ORT[s.ort] ?? s.ort}`,
                rechts: `${s.anzahl}×`
              }))}
            />
          </Liste>
          <Liste
            titel="Sofort wieder verlassen"
            wozu="Eine Bestellung wurde geöffnet und binnen 10 Sekunden verlassen — die Liste zeigte wohl das Falsche."
            leer={a.sofort_zurueck.length === 0}
          >
            <Zeilen zeilen={zaehl(a.sofort_zurueck)} />
          </Liste>
          <Liste
            titel="Abgebrochene Dialoge und weggeklickte Warnungen"
            wozu="Dialog vereinfachen oder Warnung streichen."
            leer={a.abgebrochen.length === 0 && a.warnungen.length === 0}
          >
            <Zeilen zeilen={[...zaehl(a.abgebrochen), ...zaehl(a.warnungen)]} />
          </Liste>
          <Liste
            titel="Knöpfe"
            wozu="Was in 30 Tagen niemand benutzt hat, steht oben — Kandidaten zum Entfernen."
            leer={a.knoepfe.length === 0}
          >
            <Zeilen
              zeilen={a.knoepfe.map((k) => ({
                links: k.knopf,
                rechts: k.anzahl === 0 ? 'nie' : k.anzahl === null ? `unter ${a.mindestens}×` : `${k.anzahl}×`
              }))}
            />
          </Liste>
          <Liste
            titel="Zeiten"
            wozu="Mittelwerte je Gerät — zeigen, ob eine Änderung schneller gemacht hat."
            leer={a.zeiten.length === 0}
          >
            <Zeilen
              zeilen={a.zeiten.map((z) => ({
                links: `${z.was} · ${GERAET[z.geraet] ?? z.geraet}`,
                rechts: `${sekunden(z.mittel_ms)} (${z.anzahl}×)`
              }))}
            />
          </Liste>
          <Liste titel="Fehlermeldungen" wozu="Welche Fehler wir tatsächlich sehen." leer={a.fehler.length === 0}>
            <Zeilen zeilen={zaehl(a.fehler)} />
          </Liste>
          <Liste titel="Seiten" wozu="Wie oft welche Seite geöffnet wird." leer={a.seiten.length === 0}>
            <Zeilen zeilen={zaehl(a.seiten)} />
          </Liste>
        </div>
      )}
    </AbschnittRahmen>
  );
}
